import React from 'react';
import ReactMarkdown from 'react-markdown';

import { Action } from 'redux';
import { ServiceLocator } from '../../app/ServiceLocator';
import { Messages } from '../../codegen/Localize';
import { TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { WelcomeChannelCloseButton } from '../../settings/welcomeChannelCustomization/components/WelcomeChannelCloseButton';
import { HomeStyles } from '../styles/HomeStyles';
import { WelcomeChannelStyles } from '../styles/WelcomeChannelStyles';
import { WelcomeChannelActions, WelcomeChannelLinkDetails } from '../utils/WelcomeChannelLinks';
import { WelcomeChannelLinkList } from './WelcomeChannelLink';
import { createContentButton } from '../../app/spaces/SubspaceHelpers';
import { ButtonWidget, ButtonType } from '@tableau/widgets';
import { CreateContentButtonLocations } from '../../app/spaces/Explorer/CreateContent/CreateContentButton';
import { Telemetry } from '../../commonInfra/services/Telemetry';

export interface WelcomeChannelStateProps {
  readonly header: string;
  readonly subHeader: string;
  readonly bottomLinks: WelcomeChannelLinkDetails[];
  readonly primaryButtonLink: WelcomeChannelLinkDetails;
  readonly showDefaultWelcomeChannel?: boolean;
  readonly showCreateMenu: boolean;
  readonly footer?: string;
}

export interface WelcomeChannelDispatchProps {
  readonly closeWelcomeChannel: () => any;
  readonly dispatchAction: (action: Action) => any;
}

export type WelcomeChannelProps = WelcomeChannelStateProps & WelcomeChannelDispatchProps;

export class WelcomeChannel extends React.PureComponent<WelcomeChannelProps> {

  private allowedMarkdownTypes: ReactMarkdown.NodeType[] = ['root', 'text', 'paragraph', 'link'];

  constructor(props: WelcomeChannelProps) {
    super(props);
  }

  private handleCloseButtonClick = () => {
    this.sendTelemetryEvent(WelcomeChannelActionTypes.dismiss, WelcomeChannelActions.dismiss);
    this.props.closeWelcomeChannel();
  }

  private sendTelemetryEvent = (actionType: WelcomeChannelActionTypes, action: WelcomeChannelActions) => {
    Telemetry.sendEvent({
      type: TelemetryEvents.HomeWelcomeChannel,
      actionType,
      action,
    });
  }

  render() {
    const primaryButtonAction = () => {
      this.sendTelemetryEvent(WelcomeChannelActionTypes.primary, this.props.primaryButtonLink.actionName);

      if (this.props.primaryButtonLink.getAction)
        return this.props.dispatchAction(this.props.primaryButtonLink.getAction());
      else {
        location.href = this.props.primaryButtonLink.url;
      }
    };

    const createMenu = createContentButton({
      location: CreateContentButtonLocations.WelcomeChannel,
      customButtonText: Messages.create(),
      useDarkTheme: true,
    });

    const primaryButton =
      <ButtonWidget
        buttonType={ButtonType.LightOutline}
        handleClick={primaryButtonAction}
        testId={WelcomeChannelTestIds.callToActionButton}
      >
        {this.props.primaryButtonLink.linkTitle}
      </ButtonWidget>;

    const closeButton =
      <WelcomeChannelCloseButton
        handleClick={this.handleCloseButtonClick}
        tooltipText={Messages.home_welcomeChannel_dismiss_tooltip()}
        disabled={false}
        footerOnly={!this.props.showDefaultWelcomeChannel}
      />;

    return (
      <React.Fragment>
        <section className={HomeStyles.contentSection}>
          {this.props.showDefaultWelcomeChannel && <div className={`${WelcomeChannelStyles.root} ${!this.props.footer ? HomeStyles.contentSection: ''}`} data-tb-test-id={WelcomeChannelTestIds.welcomeChannel}>
            {closeButton}
            <h2 className={WelcomeChannelStyles.header}>{this.props.header}</h2>
            <p className={WelcomeChannelStyles.subheader}>{this.props.subHeader}</p>
            {this.props.showCreateMenu ? createMenu : primaryButton}
            <WelcomeChannelLinkList
              links={this.props.bottomLinks}
              dispatchAction={this.props.dispatchAction}
              sendTelemetryEvent={this.sendTelemetryEvent}
            />
          </div>}
          {this.props.footer &&
            <footer className={WelcomeChannelStyles.footer} data-tb-test-id={WelcomeChannelTestIds.serverFooter}>
              <ReactMarkdown source={this.props.footer} escapeHtml={true} allowedTypes={this.allowedMarkdownTypes} linkTarget={'_blank'} />
              {!this.props.showDefaultWelcomeChannel && closeButton}
            </footer>
          }
        </section>
      </React.Fragment>
    );
  }
}

export enum WelcomeChannelActionTypes {
  primary = 'primary',
  secondary = 'secondary',
  dismiss = 'dismiss',
}

export namespace WelcomeChannelTestIds {
  export const welcomeChannel = 'welcome-channel';
  export const callToActionButton = 'welcome-channel-cta-button';
  export const closeButton = 'welcome-channel-close-button';
  export const closeButtonTooltip = 'welcome-channel-close-tooltip';
  export const welcomeChannelLink = 'welcome-channel-link';
  export const welcomeChannelButtonLink = 'welcome-channel-button-link';
  export const serverFooter = 'server-footer-customization';
}
